"use client";

import React from "react";
import { ArrowRight, CircleQuestionMark as HelpCircle, MessageSquareQuote, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "What makes SEO services 'affordable' without sacrificing quality?",
  answer: "Affordable SEO means delivering professional, strategic search engine optimisation while removing inflated overheads rather than cutting corners. It means focused plans – prioritising the work that delivers the greatest impact on your business – rather than trying to do everything at once. Choosing a reputable SEO provider means avoiding low-cost traps that use spammy methods. White-hat techniques focus on ethical, sustainable methods that build lasting results."
}, {
  id: "item-2",
  question: "How much do affordable SEO services cost in the UK?",
  answer: "Costs vary depending on your website size, industry, competition, and goals. Across the UK market, independent consultants typically charge £400–£1,500 per month, while agency retainers usually start at £1,500–£5,000+ per month. Most UK small businesses investing in effective SEO spend between £300 and £1,200 monthly. My affordable SEO services start from £99 per month for basic packages, while budgets under approximately £300 per month generally reflect a more focused, limited monthly scope."
}, {
  id: "item-3",
  question: "Can affordable SEO still deliver real results?",
  answer: "Yes. SEO doesn't need to cost £5,000 a month to work. When executed strategically, the SEOCaddy 2025 report found that UK SMEs investing in structured SEO achieved a median ROI of 312%. Affordable doesn't mean ineffective – it means strategic, focused, and efficient."
}, {
  id: "item-4",
  question: "What's included in your affordable SEO packages?",
  answer: "My affordable SEO services include a website SEO audit and keyword research as a minimum, plus competitor analysis, on-page SEO, technical SEO (site speed, indexing, mobile responsiveness), content optimisation, internal linking, local SEO where relevant, link building, Google Business Profile optimisation, and regular performance reporting. The exact scope depends on your business and package."
}, {
  id: "item-5",
  question: "How long before I see SEO results?",
  answer: "SEO typically takes between 3 to 6 months to show results, with more stable and significant growth usually visible around 6–12 months. Some quick wins – such as fixing technical issues or optimising existing content – can produce earlier improvements. SEO is a long-term strategy that compounds over time, not an overnight fix."
}, {
  id: "item-6",
  question: "Why choose an independent SEO consultant over an agency?",
  answer: "Working directly with an independent SEO consultant ensures your investment is focused on direct technical execution and strategic optimisation rather than agency overhead. You collaborate directly with a senior specialist, gaining clear personal accountability, agile communication, and tailored strategy."
}, {
  id: "item-7",
  question: "Do you guarantee first page Google rankings?",
  answer: "No. And you should be cautious of any SEO provider who does. Search engine algorithms consider hundreds of factors, and no one can guarantee specific positions. What I do guarantee is ethical, professional work, transparent reporting, and a strategy focused on improving your search rankings and generating measurable business outcomes."
}, {
  id: "item-8",
  question: "Can you work within my limited marketing budget?",
  answer: "Yes. I offer affordable SEO services starting from £99 per month. I work within realistic budgets by identifying high-priority areas and focusing on the highest-impact work within your available budget. I'll be honest about what's achievable at different investment levels, and I'll never recommend spending more than necessary."
}, {
  id: "item-10",
  question: "Do I need to sign a long-term contract?",
  answer: "No. I offer monthly SEO services without requiring lengthy lock-in contracts. I believe in retaining clients through results and clear communication, not contractual clauses. You should also retain administrative access to your website and analytics accounts at all times – your website is your asset."
}, {
  id: "item-11",
  question: "How do I choose an affordable SEO provider?",
  answer: "Look for transparent pricing, a clearly defined scope of deliverables, and providers who avoid unrealistic ranking guarantees. Choose a specialist who explains their methodology in plain English and can share genuine case studies or client references demonstrating sustainable, ethical results."
}, {
  id: "item-12",
  question: "Can SEO support scale as my business grows?",
  answer: "Yes. Engagement scopes can readily expand over time as your budget, priorities, or website scale change, allowing you to increase strategic and technical support without requiring rigid long-term commitments upfront."
}];
export default function AffordableFaq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map(item => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
  return <section id="affordableseofaq" data-nav="light" className="relative bg-background py-16 md:py-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center">
          <div className="inline-flex items-center justify-center">
            <Badge variant="outline" className="gap-1.5 border-border bg-muted/60 px-3 py-1 text-xs font-semibold text-foreground uppercase tracking-wide">
              <HelpCircle className="size-3.5 text-primary" />
              Direct Answers
            </Badge>
          </div>

          <h2 className="mt-4 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Frequently Asked Questions About Affordable SEO Services
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-default text-base text-muted-foreground sm:text-lg">
            Clear, upfront guidance on pricing, deliverables, timelines, and how working directly with an independent SEO consultant compares to traditional agencies.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="mt-12">
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-4 sm:p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqList.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border-b border-border py-2 first:pt-0 last:border-b-0">
                    <AccordionTrigger className="text-left font-default text-base font-semibold text-foreground transition-colors hover:text-primary sm:text-lg">
                      <span className="flex items-start gap-3">
                        <span className="font-mono text-xs font-medium text-primary sm:text-sm">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span>{item.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pl-7 text-sm leading-relaxed text-muted-foreground sm:pl-8 sm:text-base">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/40 p-8 sm:p-10">
            <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                  <Sparkles className="size-4" />
                  Personalised Advice
                </div>
                <h3 className="mt-2 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Have a unique SEO challenge? Talk directly with me.
                </h3>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                  No sales managers or generic packages. Share your website goals and receive honest, pragmatic recommendations tailored to your exact budget.
                </p>
              </div>

              <div className="shrink-0">
                <Button asChild size="lg" className="rounded-lg font-medium shadow-sm">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    <MessageSquareQuote className="size-4" />
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}